// Cross-product handoff (Figma 61:562 / 66:310, Gate 10/14). Switching
// products means a real cross-origin navigation between separately
// authenticated apps (ai./code./design./p./forward.*). Only non-secret
// CONTEXT crosses that boundary -- never authority. The target product
// still independently re-authenticates, and re-checks project membership
// and tool policy; nothing here substitutes for that.
//
// The allowlist below is the enforcement mechanism: buildProductHandoffUrl
// reads ONLY these named fields off the context object. A field like
// approvalToken or role accidentally placed on the context is structurally
// unreachable -- there is no code path from "field exists on the object" to
// "field appears in the URL" for anything not in ALLOWED_PARAMS.
export interface ProductHandoffContext {
  tenantId?: string | null;
  projectId?: string | null;
  language?: string | null;
  theme?: 'light' | 'dark' | null;
  artifactRef?: string | null;
  returnPath?: string | null;
}

const ALLOWED_PARAMS = Object.freeze([
  'tenantId', 'projectId', 'language', 'theme', 'artifactRef', 'returnPath',
] as const);

export function buildProductHandoffUrl(baseUrl: string, context: ProductHandoffContext): string {
  const url = new URL(baseUrl);
  for (const key of ALLOWED_PARAMS) {
    const value = context[key];
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

// Reads only the allowlisted context fields back out of a URL the target
// product receives -- the read-side mirror of the write-side allowlist
// above, so a target product's own parsing can't be tricked into trusting
// an unexpected query param as if it were handoff context.
export function readProductHandoffContext(url: string | URL): ProductHandoffContext {
  const parsed = typeof url === 'string' ? new URL(url) : url;
  const context: ProductHandoffContext = {};
  for (const key of ALLOWED_PARAMS) {
    const value = parsed.searchParams.get(key);
    if (value !== null) {
      if (key === 'theme') context.theme = value === 'dark' ? 'dark' : 'light';
      else (context as Record<string, string>)[key] = value;
    }
  }
  return context;
}
